// CDN entry point — self-contained IIFE bundle for <script> tag usage.
// Exposes window.layers with pre-init event buffering.
//
// Usage:
//   <script src="https://cdn.layers.com/sdk/v1/layers.min.js"></script>
//   <script>
//     layers.init({ appId: 'my-app', environment: 'production' });
//     layers.track('page_view', { page: '/home' });
//   </script>
//
// layers_core_bg.wasm must be published beside layers.min.js — the bundle
// fetches it as a sibling of its own <script src>. scripts/build-cdn.mjs stages
// it into packages/client/cdn/ and scripts/deploy-cdn.sh uploads it to both the
// /sdk/v1/ and /sdk/v1/<version>/ prefixes.
import { initWasm } from '@layers/core-wasm';

import { type ErrorListener, LayersClient, type LayersConfig } from './index.js';

type BufferedCall =
  | ['track', string, Record<string, unknown> | undefined]
  | ['screen', string, Record<string, unknown> | undefined]
  | ['setAppUserId', string | undefined]
  | ['setConsent', { analytics?: boolean; advertising?: boolean }]
  | ['setUserProperties', Record<string, unknown>]
  | ['setUserPropertiesOnce', Record<string, unknown>];

/** Binary the wasm-bindgen glue looks for beside itself. */
const WASM_BINARY = 'layers_core_bg.wasm';

/**
 * Filename this bundle is published under, used only by the `<script src>` scan
 * in loadingScriptUrl().
 *
 * Injected from the real `outfile` by scripts/build-cdn.mjs — the same
 * define-with-a-fallback shape `__LAYERS_CLIENT_VERSION__` uses — so renaming
 * the output cannot leave this pointing at a filename nobody publishes. The
 * literal is only what `tsc` and the test runner see.
 */
declare const __LAYERS_CDN_FILENAME__: string;
const BUNDLE_FILENAME =
  typeof __LAYERS_CDN_FILENAME__ !== 'undefined' ? __LAYERS_CDN_FILENAME__ : 'layers.min.js';

/**
 * Does this `<script src>` point at our own bundle?
 *
 * `HTMLScriptElement.src` reflects the resolved absolute URL, so no base is
 * needed. A src that will not parse is somebody else's problem.
 */
function isThisBundle(src: string): boolean {
  try {
    return new URL(src).pathname.endsWith(`/${BUNDLE_FILENAME}`);
  } catch {
    return false;
  }
}

/**
 * URL of the `<script>` tag this bundle was loaded from, or undefined when it
 * cannot be determined.
 *
 * `document.currentScript` is only non-null while the script is executing, and
 * only for classic scripts. Both restrictions are fine here and neither is
 * incidental — see SCRIPT_RELATIVE_WASM_URL for why this is read at module
 * scope rather than inside init().
 *
 * The `<script src>` scan is the second chance for the one common case
 * `currentScript` does not cover: `<script type="module" src="...">`, where the
 * spec sets `currentScript` to null. Last match wins because a page that
 * somehow carries two copies should use the one that executed last.
 */
function loadingScriptUrl(): string | undefined {
  try {
    if (typeof document === 'undefined') return undefined;
    const current = document.currentScript as HTMLScriptElement | null;
    if (current?.src) return current.src;
    const scripts = Array.from(document.getElementsByTagName('script')).reverse();
    return scripts.find((script) => script.src && isThisBundle(script.src))?.src;
  } catch {
    // No DOM, or a hostile document. Fall through to undefined and let the
    // glue's own resolution stand.
    return undefined;
  }
}

/**
 * Where this bundle's WASM binary lives, resolved from the script tag that
 * loaded it. `undefined` when the script cannot locate itself.
 *
 * THIS MUST STAY AT MODULE SCOPE.
 *
 * esbuild emits the entry module's body inline in the IIFE, so this runs while
 * the classic `<script>` is still executing — the only window in which
 * `document.currentScript` is non-null. Move it inside init() and it reads
 * null, because init() is called from a later script (or after an await) and
 * the CDN bundle silently goes back to the JS engine.
 *
 * The glue's own default — `new URL(WASM_BINARY, import.meta.url)` — cannot
 * work here: esbuild substitutes an empty object for `import.meta` in an IIFE
 * (it warns `empty-import-meta` on every build), `undefined` is not a valid
 * URL base, and the resulting `TypeError: Invalid URL` was caught by the
 * loader and reported as `engine/js` on the wire. Every release of
 * cdn.layers.com/sdk/v1/layers.min.js shipped that way; the 856KB binary was
 * never requested by any of them.
 */
const SCRIPT_RELATIVE_WASM_URL: string | undefined = (() => {
  const base = loadingScriptUrl();
  if (base === undefined) return undefined;
  try {
    return new URL(WASM_BINARY, base).href;
  } catch {
    return undefined;
  }
})();

let client: LayersClient | null = null;
let initPromise: Promise<void> | null = null;
const buffer: BufferedCall[] = [];
const pendingErrorListeners: ErrorListener[] = [];

function replayBuffer(c: LayersClient): void {
  for (const listener of pendingErrorListeners) {
    c.on('error', listener);
  }
  pendingErrorListeners.length = 0;
  for (const call of buffer) {
    const [method, ...args] = call;
    switch (method) {
      case 'track':
        c.track(args[0] as string, args[1] as Record<string, unknown>);
        break;
      case 'screen':
        c.screen(args[0] as string, args[1] as Record<string, unknown>);
        break;
      case 'setAppUserId':
        c.setAppUserId(args[0] as string | undefined);
        break;
      case 'setConsent':
        c.setConsent(args[0] as { analytics?: boolean; advertising?: boolean });
        break;
      case 'setUserProperties':
        c.setUserProperties(args[0] as Record<string, unknown>);
        break;
      case 'setUserPropertiesOnce':
        c.setUserPropertiesOnce(args[0] as Record<string, unknown>);
        break;
    }
  }
  buffer.length = 0;
}

const layers = {
  async init(config: LayersConfig): Promise<void> {
    if (client || initPromise) {
      console.warn('[Layers] Already initialized');
      if (initPromise) await initPromise;
      return;
    }
    // An explicit `wasmUrl` always wins — it is how a consumer self-hosts the
    // binary. Otherwise resolve it beside the script tag, which is where the
    // release pipeline uploads it.
    const wasmUrl = config.wasmUrl ?? SCRIPT_RELATIVE_WASM_URL;
    initPromise = (async () => {
      try {
        await initWasm(wasmUrl === undefined ? undefined : { wasmUrl });
        // Hand the same URL to the client so its own initWasm() call agrees
        // with this one instead of retrying against the unusable default.
        const c = new LayersClient(wasmUrl === undefined ? config : { ...config, wasmUrl });
        await c.init();
        client = c;
        replayBuffer(c);
      } catch (e) {
        initPromise = null;
        console.error('[Layers] init failed:', e);
        throw e;
      }
    })();
    await initPromise;
  },

  track(eventName: string, properties?: Record<string, unknown>): void {
    if (client) {
      client.track(eventName, properties);
    } else {
      buffer.push(['track', eventName, properties]);
    }
  },

  screen(screenName: string, properties?: Record<string, unknown>): void {
    if (client) {
      client.screen(screenName, properties);
    } else {
      buffer.push(['screen', screenName, properties]);
    }
  },

  setAppUserId(userId: string | undefined): void {
    if (client) {
      client.setAppUserId(userId);
    } else {
      buffer.push(['setAppUserId', userId]);
    }
  },

  setConsent(consent: { analytics?: boolean; advertising?: boolean }): void {
    if (client) {
      client.setConsent(consent);
    } else {
      buffer.push(['setConsent', consent]);
    }
  },

  setUserProperties(properties: Record<string, unknown>): void {
    if (client) {
      client.setUserProperties(properties);
    } else {
      buffer.push(['setUserProperties', properties]);
    }
  },

  setUserPropertiesOnce(properties: Record<string, unknown>): void {
    if (client) {
      client.setUserPropertiesOnce(properties);
    } else {
      buffer.push(['setUserPropertiesOnce', properties]);
    }
  },

  async flush(): Promise<void> {
    if (client) await client.flush();
  },

  shutdown(): void {
    if (client) client.shutdown();
    client = null;
    initPromise = null;
  },

  async shutdownAsync(timeoutMs?: number): Promise<void> {
    if (client) await client.shutdownAsync(timeoutMs);
    client = null;
    initPromise = null;
  },

  getSessionId(): string | undefined {
    return client?.getSessionId();
  },

  getAppUserId(): string | undefined {
    return client?.getAppUserId();
  },

  on(event: 'error', listener: ErrorListener): void {
    if (client) {
      client.on(event, listener);
    } else {
      pendingErrorListeners.push(listener);
    }
  },

  off(event: 'error', listener: ErrorListener): void {
    if (client) {
      client.off(event, listener);
    } else {
      const idx = pendingErrorListeners.indexOf(listener);
      if (idx >= 0) pendingErrorListeners.splice(idx, 1);
    }
  }
};

(window as any).layers = layers;
